interface SentryFrame {
  filename?: string;
}

interface SentryEvent {
  exception?: {
    values?: { stacktrace?: { frames?: SentryFrame[] } }[];
  };
}

// O CRA publica o bundle em /static/js/main.<hash>.js — é o que aparece no `filename` dos
// frames vindos do nosso código. Script de extensão chega como `<anonymous>` ou
// chrome-extension:// e nunca casa com isto.
const APP_BUNDLE = /\/static\/js\//;

/**
 * `beforeSend` do Sentry: descarta erro cujo stack é inteiramente de código de terceiro
 * rodando na aba do usuário (extensão de navegador, agente corporativo).
 *
 * Motivo: uma extensão que sobrescreve `HTMLDivElement.prototype.appendChild` tentou fazer
 * `JSON.stringify` de um `HTMLAnchorElement`; como o React 19 pendura o fiber no nó DOM
 * (`__reactFiber$… -> stateNode -> nó`), a serialização entra em ciclo e estoura
 * "Converting circular structure to JSON". O erro sobe no escopo global da página e cai no
 * nosso projeto, mas não há nada a corrigir no produto.
 *
 * Filtra por ORIGEM, não por mensagem: filtrar a string "Converting circular structure to
 * JSON" silenciaria também um bug nosso que produzisse a mesma mensagem.
 *
 * **Fail-open de propósito:** evento sem frame nenhum é mantido. Boa parte do que reportamos
 * vem de `captureException` com objeto de erro da API, que não tem stack — descartar por
 * ausência de frame apagaria justamente a instrumentação que acabamos de adicionar.
 */
export default function dropThirdPartyEvents<T extends SentryEvent>(event: T): T | null {
  const frames = (event.exception?.values ?? [])
    .flatMap((value) => value.stacktrace?.frames ?? []);

  if (!frames.length) return event;

  const hasAppFrame = frames.some((frame) => APP_BUNDLE.test(frame.filename ?? ''));

  return hasAppFrame ? event : null;
}
